import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import { Label } from "./components/ui/label";
import { Checkbox } from "./components/ui/checkbox";
import { encryptData, decryptData } from './encryptionUtil';
import { useLocation } from 'react-router-dom';
import HelpIcon from './components/ui/HelpIcon';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { SimpleCalendar } from './components/ui/simple-calendar';
import { CalendarIcon, ChevronDown, ChevronRight, Check, AlertCircle, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from './lib/utils';
import { geocodePostcode } from './utils/geolocationService';
import { useTheme } from './components/ui/theme-provider';
import PracticeSettings from './components/PracticeSettings';
import { PracticeDataProvider } from './contexts/PracticeDataContext';
import { practiceDataUtil } from './helpers/practiceDataUtil';

// Collapsible section component
const CollapsibleSection = ({ title, subtitle, isComplete, isOpen, onToggle, children, theme }) => {
  return (
    <div className={cn(
      "border rounded-lg overflow-hidden transition-all duration-200",
      theme === 'light' ? 'border-gray-200' : 'border-border',
      isOpen ? '' : 'hover:border-purple-500/30'
    )}>
      {/* Section Header — always visible */}
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "w-full flex items-center justify-between p-4 text-left transition-colors",
          theme === 'light' 
            ? 'hover:bg-gray-50' 
            : 'hover:bg-muted/20',
          isOpen && (theme === 'light' ? 'bg-gray-50' : 'bg-muted/10')
        )}
      >
        <div className="flex items-center gap-3">
          {/* Completion indicator */}
          <div className={cn(
            "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center",
            isComplete 
              ? 'bg-green-500/20 text-green-600' 
              : 'bg-amber-500/20 text-amber-600'
          )}>
            {isComplete ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
          </div>
          <div>
            <h3 className="text-base font-semibold text-accent-foreground">{title}</h3>
            {!isOpen && subtitle && (
              <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        {/* Chevron */}
        {isOpen 
          ? <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" /> 
          : <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        }
      </button>
      {/* Section Content — collapsible */}
      {isOpen && (
        <div className={cn(
          "px-4 pb-4 pt-2 border-t",
          theme === 'light' ? 'border-gray-100' : 'border-border/50'
        )}>
          {children}
        </div>
      )}
    </div>
  );
};

const ProfileSettings = ({ profileData: initialProfileData, onSave, onClose }) => {
  const { theme } = useTheme(); // Initialize theme here
  const [profile, setProfile] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    postcode: '',
    latitude: '', // ADDED: For storing latitude
    longitude: '', // ADDED: For storing longitude
    role: '', // ADDED: For storing user role
    bankName: '',
    sortCode: '',
    accountNumber: '',
    gdcNumber: '',
    gdcExpiryDate: null,
    gdcProvider: 'General Dental Council', // Default GDC provider
    gdcLastUpdated: null,
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceExpiryDate: null,
    insuranceLastUpdated: null,
    consentPreferences: {
      email: {
        gdcInsuranceReminders: false,
        marketingUpdates: false,
        systemMaintenanceUpdates: true,
      }
    }
  });
  
  // ADDED: State to store the initially loaded profile for comparison
  const [initialProfileForComparison, setInitialProfileForComparison] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState(''); // Used for inline success/error messages
  const [validationErrors, setValidationErrors] = useState({}); // ADDED: For field validation errors
  const location = useLocation();
  const auth = getAuth();
  const db = getFirestore();

  // Tracks sensitive fields whose ciphertext failed to decrypt on load (wrong key /
  // corrupted data). handleSave excludes these from the write payload so a failed
  // decrypt doesn't silently overwrite the real encrypted value in Firestore.
  const decryptionFailedFieldsRef = useRef(new Set());

  // State for popover visibility
  const [gdcCalendarOpen, setGdcCalendarOpen] = useState(false);
  const [insuranceCalendarOpen, setInsuranceCalendarOpen] = useState(false);

  // Practice management state
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [showPracticeSettings, setShowPracticeSettings] = useState(false);

  // Collapsible section state
  const [openSections, setOpenSections] = useState({
    practices: false,
    personal: true,
    banking: true,
    registrations: true,
    communications: true,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Completion checks for each section
  const isPersonalComplete = !!(
    profile.name && profile.name.trim() !== '' &&
    profile.role && profile.role.trim() !== '' &&
    profile.address1 && profile.address1.trim() !== '' &&
    profile.city && profile.city.trim() !== '' &&
    profile.postcode && profile.postcode.trim() !== ''
  );

  const isBankingComplete = !!(
    profile.bankName && profile.bankName.trim() !== '' &&
    profile.sortCode && profile.sortCode.trim() !== '' &&
    profile.accountNumber && profile.accountNumber.trim() !== ''
  );

  const isRegistrationsComplete = !!(
    profile.gdcNumber && profile.gdcNumber.trim() !== '' &&
    profile.gdcExpiryDate
  );

  const isCommunicationsComplete = true; // Always "complete" — preferences are optional

  const isPracticesComplete = selectedPractices.length > 0;

  const fieldOrder = ['name', 'address1', 'address2', 'city', 'postcode'];
  // Banking fields removed from main fieldOrder - they're now in their own conditional section

  const fieldLabels = {
    name: 'Full Name',
    address1: 'Address Line 1',
    address2: 'Address Line 2',
    city: 'City',
    postcode: 'Postcode',
    role: 'Professional Role',
    // Banking field labels moved to the conditional banking section
    bankName: 'Bank Name',
    sortCode: 'Bank Sort Code',
    accountNumber: 'Account Number'
    // ... any other existing field labels ...
  };

  const sensitiveFields = [
    'name', 'address1', 'address2', 'city', 'postcode',
    'latitude', // ADDED: latitude to sensitive fields for encryption
    'longitude', // ADDED: longitude to sensitive fields for encryption
    'bankName', 'accountNumber', 'sortCode', 'lastInvoiceNumber',
    'gdcNumber', 
    'insurancePolicyNumber',
    'insuranceProvider'
    // Note: expiryDate for GDC/Insurance and consentPreferences are not typically encrypted
  ];

  const decryptProfileData = useCallback((encryptedData) => {
    const decrypted = { ...encryptedData };

    // Initialize consent preferences if not present
    if (!decrypted.consentPreferences || !decrypted.consentPreferences.email) {
      decrypted.consentPreferences = {
        email: {
          gdcInsuranceReminders: false,
          marketingUpdates: false,
          systemMaintenanceUpdates: true,
        }
      };
    }

    // Decrypt top-level sensitive fields
    decryptionFailedFieldsRef.current.clear();
    for (const field of sensitiveFields) {
      if (decrypted[field] && typeof decrypted[field] === 'string' && decrypted[field].startsWith('U2FsdGVkX1')) {
        const result = decryptData(decrypted[field]);
        // decryptData returns '' both for a legitimate empty decryption and for a
        // failed one (wrong key / corrupted ciphertext). Since the raw value here
        // was confirmed to look encrypted (starts with the AES prefix), an empty
        // result means decryption failed. Track it so handleSave can exclude this
        // field from the write payload instead of persisting '' over real data.
        if (!result) {
          decryptionFailedFieldsRef.current.add(field);
        }
        decrypted[field] = result;
      } else if (decrypted[field] === undefined) {
        decrypted[field] = ''; // Ensure field exists if undefined
      }
    }
    
    // Convert date strings/Timestamps from Firestore to Date objects for date inputs
    const gdcExpiry = encryptedData.gdcExpiryDate;
    const insuranceExpiry = encryptedData.insuranceExpiryDate;

    decrypted.gdcExpiryDate = gdcExpiry ? 
        (gdcExpiry.toDate ? gdcExpiry.toDate() : new Date(gdcExpiry)) : null;
    decrypted.insuranceExpiryDate = insuranceExpiry ? 
        (insuranceExpiry.toDate ? insuranceExpiry.toDate() : new Date(insuranceExpiry)) : null;
    
    // Ensure GDC provider is set if not already
    if (!decrypted.gdcProvider) {
        decrypted.gdcProvider = 'General Dental Council';
    }

    // Remove old professionalRegistrations structure if it exists to avoid confusion
    delete decrypted.professionalRegistrations;

    return decrypted;
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (auth.currentUser) {
        setLoading(true);
        setError('');
        setSaveStatus(''); // Clear status messages
        try {          if (initialProfileData && Object.keys(initialProfileData).length > 1) {
            if (process.env.NODE_ENV === 'development') {
              console.log("ProfileSettings: Using initialProfileData prop", initialProfileData);
            }
            const processedProfileData = decryptProfileData(initialProfileData);
            setProfile(prev => ({ ...prev, ...processedProfileData }));
            setInitialProfileForComparison(JSON.parse(JSON.stringify(processedProfileData)));
            // Load selected practices from profile data
            if (initialProfileData.selectedPractices) {
              resolvePracticeCodes(initialProfileData.selectedPractices);
            }
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log("ProfileSettings: Fetching profile from Firestore for user", auth.currentUser.uid);
            }
            const userDoc = doc(db, 'users', auth.currentUser.uid);
            const userSnapshot = await getDoc(userDoc);
            if (userSnapshot.exists()) {
              const rawData = userSnapshot.data();
              const decryptedProfile = decryptProfileData(rawData);
              setProfile(prev => ({ ...prev, ...decryptedProfile }));
              setInitialProfileForComparison(JSON.parse(JSON.stringify(decryptedProfile)));
              // Load selected practices from Firestore
              if (rawData.selectedPractices) {
                resolvePracticeCodes(rawData.selectedPractices);
              }
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.log("ProfileSettings: No profile document found, using defaults.");
              }
              setProfile(prev => ({
                ...prev,
                email: auth.currentUser.email || '',
                name: auth.currentUser.displayName || '',
                gdcProvider: 'General Dental Council', // Ensure default GDC provider
              }));
            }
          }        } catch (fetchError) {
          if (process.env.NODE_ENV === 'development') {
            console.error("Error fetching/decrypting profile:", fetchError);
          }
          setError("Failed to load profile data. Please try again.");
        } finally {
          setLoading(false);
        }
      } else {
        setError("Not authenticated. Cannot load profile.");
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, [auth.currentUser, db, initialProfileData, decryptProfileData]);

  // Auto-collapse completed sections once data loads
  useEffect(() => {
    if (!loading && profile.name) {
      setOpenSections({
        practices: !isPracticesComplete,
        personal: !isPersonalComplete,
        banking: !isBankingComplete,
        registrations: !isRegistrationsComplete,
        communications: false, // Always start collapsed since it's optional
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);


  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    // For consentPreferences
    const [mainKey, subKey, nestedKey] = id.split('.'); 

    setProfile(prev => {
      if (mainKey === 'consentPreferences' && subKey === 'email' && nestedKey) {
        return {
          ...prev,
          consentPreferences: {
            ...prev.consentPreferences,
            email: {
              ...prev.consentPreferences.email,
              [nestedKey]: checked
            }
          }
        };
      }
      return { ...prev, [id]: value };
    });

    // ADDED: Clear validation error when user starts typing/selecting
    if (validationErrors[id]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const handleDateChange = (id, newDate) => { 
    setProfile(prev => ({
      ...prev,
      [id]: newDate || null 
    }));
    // Close respective calendar
    if (id === 'gdcExpiryDate') setGdcCalendarOpen(false);
    if (id === 'insuranceExpiryDate') setInsuranceCalendarOpen(false);
  };

  // Resolve practice codes to full practice objects with names
  const resolvePracticeCodes = async (practices) => {
    if (!practices || practices.length === 0) {
      setSelectedPractices([]);
      return;
    }
    // Check if they're already full objects
    const firstItem = practices[0];
    if (typeof firstItem === 'object' && firstItem.prac_name) {
      setSelectedPractices(practices);
      return;
    }
    // They're just codes — resolve them
    const codes = practices.map(p => typeof p === 'object' ? (p.prac_code || p) : p);
    try {
      const resolved = await practiceDataUtil.getPracticesByCodes(codes);
      if (resolved && resolved.length > 0) {
        setSelectedPractices(resolved);
      } else {
        // Fallback: keep as codes wrapped in objects
        setSelectedPractices(codes.map(code => ({ prac_code: code, prac_name: code })));
      }
    } catch (err) {
      console.error('[ProfileSettings] Error resolving practice codes:', err);
      // Fallback
      setSelectedPractices(codes.map(code => ({ prac_code: code, prac_name: code })));
    }
  };

  // Handle saving practices from PracticeSettings dialog
  const handleSavePractices = async (updatedPractices, newlyAddedPracticeCode = null) => {
    if (!auth.currentUser) return;
    try {
      setSelectedPractices(updatedPractices);
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        selectedPractices: updatedPractices.map(p => p.prac_code || p),
        lastUpdated: new Date()
      });
      setShowPracticeSettings(false);
      if (process.env.NODE_ENV === 'development') {
        console.log('[ProfileSettings] Practices saved:', updatedPractices.length);
      }
    } catch (err) {
      console.error('[ProfileSettings] Error saving practices:', err);
      setError('Failed to save practice selection.');
    }
  };
  
  const handleSave = async () => {
    if (auth.currentUser) {
      try {
        // ENHANCED: Comprehensive validation for required fields
        const newValidationErrors = {};
        
        // Role is absolutely required
        if (!profile.role || profile.role.trim() === '') {
          newValidationErrors.role = 'Please select your professional role';
        }

        // Basic profile fields validation - validate the encrypted 'name' field
        if (!profile.name || profile.name.trim() === '') {
          newValidationErrors.name = 'Full name is required';
        }

        if (Object.keys(newValidationErrors).length > 0) {
          setValidationErrors(newValidationErrors);
          setError('Please correct the errors below before saving.');
          // Scroll to top to show errors
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return; // Stop the save process
        }

        setSaveStatus('Saving...');
        setError('');
        setValidationErrors({});
        
        const cleanedProfile = { ...profile };
        const authPropertiesToRemove = [
          'apiKey', 'appName', 'authDomain', 'stsTokenManager',
          'refreshToken', 'proactiveRefresh', 'reloadUserInfo', 'reloadListener',
          'uid', 'auth', 'accessToken', 'emailVerified', 'isAnonymous', 
          'metadata', 'providerData', 'tenantId', '_redirectEventId'
        ];
        authPropertiesToRemove.forEach(prop => delete cleanedProfile[prop]);

        let dataToSave = { ...cleanedProfile };

        // Set displayName to match the name field for downstream compatibility
        if (dataToSave.name) {
          dataToSave.displayName = dataToSave.name;
        }

        // --- BEGIN INTEGRATION OF GEOCODING LOGIC ---
        // This block should be placed *before* you encrypt `dataToSave`.

        if (profile.postcode && initialProfileForComparison) { // Ensure initialProfileForComparison is loaded
          const postcodeChanged = profile.postcode !== initialProfileForComparison.postcode;
          // Check if latitude is missing from the initially loaded data (not just current profile state)
          const coordinatesMissingOrOutdated = !initialProfileForComparison.latitude || postcodeChanged;          if (postcodeChanged || coordinatesMissingOrOutdated) {
            if (process.env.NODE_ENV === 'development') {
              console.log("ProfileSettings: Postcode changed or coordinates missing/outdated, attempting geocode for:", profile.postcode);
            }
            try {
              const coordinates = await geocodePostcode(profile.postcode);
              if (coordinates && coordinates.latitude && coordinates.longitude) {                // Update the object you intend to save (e.g., dataToSave)
                dataToSave.latitude = coordinates.latitude;
                dataToSave.longitude = coordinates.longitude;
                if (process.env.NODE_ENV === 'development') {
                  console.log("ProfileSettings: Geocoding successful. Lat:", dataToSave.latitude, "Lon:", dataToSave.longitude);
                }
              } else {
                if (process.env.NODE_ENV === 'development') {
                  console.warn("ProfileSettings: Geocoding failed or no results. Saving profile without new coordinates.");
                }
                // If postcode changed but geocoding failed, you might want to clear old coordinates
                if (postcodeChanged) {
                  dataToSave.latitude = ''; 
                  dataToSave.longitude = '';
                }
              }            } catch (geoError) {
              if (process.env.NODE_ENV === 'development') {
                console.error("ProfileSettings: Error during geocoding API call:", geoError);
              }
              // Decide how to handle API errors, e.g., save without coordinates
            }          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log("ProfileSettings: Postcode not changed and coordinates likely exist, skipping geocode.");
            }
            // Ensure existing lat/lon are carried over if not geocoding this time
            dataToSave.latitude = profile.latitude || initialProfileForComparison?.latitude || '';
            dataToSave.longitude = profile.longitude || initialProfileForComparison?.longitude || '';
          }        } else if (profile.postcode) {
           // Handle case where initialProfileForComparison might not be set yet, but we have a postcode (e.g. new profile)
           if (process.env.NODE_ENV === 'development') {
             console.log("ProfileSettings: Attempting geocode for new profile or missing initial data for postcode:", profile.postcode);
           }
           try {
             const coordinates = await geocodePostcode(profile.postcode);
             if (coordinates && coordinates.latitude && coordinates.longitude) {
               dataToSave.latitude = coordinates.latitude;
               dataToSave.longitude = coordinates.longitude;
             }           } catch (geoError) {
             if (process.env.NODE_ENV === 'development') {
               console.error("ProfileSettings: Error during geocoding API call:", geoError);
             }
           }
        }
        // --- END INTEGRATION OF GEOCODING LOGIC ---

        // Exclude any field that failed to decrypt on load BEFORE encryption —
        // dataToSave[field] would be '' for these, and encrypting '' produces
        // ciphertext that would permanently wipe the real value via merge:true.
        // Omitting the key means setDoc's merge:true leaves the existing Firestore
        // value untouched.
        if (decryptionFailedFieldsRef.current.size > 0) {
          decryptionFailedFieldsRef.current.forEach((field) => {
            delete dataToSave[field];
          });
          if (process.env.NODE_ENV === 'development') {
            console.warn('ProfileSettings: Skipping save for fields that failed to decrypt:', Array.from(decryptionFailedFieldsRef.current));
          }
        }

        // Encrypt remaining sensitive fields (now top-level)
        for (const field of sensitiveFields) {
          if (dataToSave[field] && typeof dataToSave[field] === 'string') {
            dataToSave[field] = encryptData(dataToSave[field]);
          }
        }
        
        // Convert expiry dates to Firestore Timestamps before saving (now top-level)
        if (dataToSave.gdcExpiryDate) {
          dataToSave.gdcExpiryDate = Timestamp.fromDate(new Date(dataToSave.gdcExpiryDate));
          dataToSave.gdcLastUpdated = Timestamp.now();
        }
        if (dataToSave.insuranceExpiryDate) {
          dataToSave.insuranceExpiryDate = Timestamp.fromDate(new Date(dataToSave.insuranceExpiryDate));
          dataToSave.insuranceLastUpdated = Timestamp.now();
        }

        // Ensure GDC provider is always set
        dataToSave.gdcProvider = 'General Dental Council';

        // Remove old professionalRegistrations structure if it somehow persists
        delete dataToSave.professionalRegistrations;

        // Set profile as complete only if role is selected AND name is provided
        dataToSave.profileComplete = !!(profile.role && profile.role.trim() !== '' && profile.name && profile.name.trim() !== '');
        
        dataToSave.lastUpdated = Timestamp.now(); // Use server timestamp or client's new Date()

        const userDoc = doc(db, 'users', auth.currentUser.uid);
        await setDoc(userDoc, dataToSave, { merge: true });
        
        if (decryptionFailedFieldsRef.current.size > 0) {
          setSaveStatus('Profile saved, but some encrypted fields could not be read and were left unchanged. Please contact support if this persists.');
        } else {
          setSaveStatus('Profile saved successfully');
        }
        // setError(''); // Clear error on success
        
        if (onSave) { // onSave is the prop from App.js
          // Pass the locally held 'profile' state (decrypted)
          // App.js's onSave handler will take care of everything else, including navigation
          await onSave(profile); 
        }
          // Navigation is now handled by the onSave prop in App.js
        // const isOnboarding = location.state?.onboarding;
        // if (typeof onClose === 'function' && !isOnboarding) {
        //   onClose(); // This was causing navigation before App.js could update state fully.
        // }
      } catch (saveError) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Error updating profile:", saveError);
        }
        setError("Failed to save profile. Please try again.");
        setSaveStatus(''); // Clear save status on error
      }
    }
  };

  // ENHANCED: Navigation protection with role-aware messaging
  const handleClose = () => {
    const missingRole = !profile.role || profile.role.trim() === '';
    const missingName = !profile.name || profile.name.trim() === '';  // Check the encrypted name field
    
    if (missingRole || missingName) {
      let message = "Please complete your profile to continue.";
      
      if (missingRole && missingName) {
        message = "Please complete your profile and select your professional role to continue.";
      } else if (missingRole) {
        message = "Please select your professional role to continue.";
      } else if (missingName) {
        message = "Please enter your full name to continue.";
      }
      
      setError(message);
      return; // Prevent navigation
    }
    
    // Allow navigation if requirements are met
    if (onClose) {
      onClose();
    }
  };

  if (loading) {
    return <div className="text-purple-800 p-6">Loading profile...</div>;
  }

  return (
    <>
    <Card className="rounded-lg border-border shadow-sm w-full max-w-2xl mx-auto app-card-bg overflow-y-auto max-h-[80vh]">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 p-6 pb-2">
        <div>
          <CardTitle className="text-2xl font-bold text-accent-foreground">Profile Settings</CardTitle>
          <p className="text-sm text-muted-foreground pt-1">
            Complete your profile and select your professional role to continue.
          </p>
        </div>
        {/* ENHANCED: Only show close button if profile requirements are met */}
        {(profile.role && profile.role.trim() !== '' && profile.name && profile.name.trim() !== '') && (
          <Button variant="ghost" size="sm" onClick={handleClose}>
            ×
          </Button>
        )}
        <HelpIcon sectionKey="ProfileSettings" />
      </CardHeader>
      
      <CardContent className="p-6 pt-4 space-y-4">
        {/* Section completion overview */}
        <div className="bg-muted/30 p-3 rounded-md">
          <h4 className="text-sm font-medium text-accent-foreground mb-2">Profile Completion:</h4>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full", isPracticesComplete ? 'bg-green-500/20 text-green-600' : 'bg-amber-500/20 text-amber-600')}>
              {isPracticesComplete ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />} Practices ({selectedPractices.length})
            </span>
            <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full", isPersonalComplete ? 'bg-green-500/20 text-green-600' : 'bg-amber-500/20 text-amber-600')}>
              {isPersonalComplete ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />} Personal Info
            </span>
            {profile.role !== 'Practice Manager' && (
              <>
                <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full", isBankingComplete ? 'bg-green-500/20 text-green-600' : 'bg-amber-500/20 text-amber-600')}>
                  {isBankingComplete ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />} Banking
                </span>
                <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full", isRegistrationsComplete ? 'bg-green-500/20 text-green-600' : 'bg-amber-500/20 text-amber-600')}>
                  {isRegistrationsComplete ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />} Registrations
                </span>
              </>
            )}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-600">
              <Check className="h-3 w-3" /> Communications
            </span>
          </div>
        </div>

        {/* ─── Section 0: Practice Management ─── */}
        <CollapsibleSection
          title="My Practices"
          subtitle={isPracticesComplete ? `${selectedPractices.length} practice${selectedPractices.length !== 1 ? 's' : ''} selected` : 'No practices added yet'}
          isComplete={isPracticesComplete}
          isOpen={openSections.practices}
          onToggle={() => toggleSection('practices')}
          theme={theme}
        >
          <p className="text-sm text-muted-foreground mb-4">
            Manage the dental practices you work with. These are the practices available when creating invoices.
          </p>
          
          {/* Show current practices list */}
          {selectedPractices.length > 0 ? (
            <div className="space-y-2 mb-4">
              {selectedPractices.map((practice, index) => {
                const isObject = typeof practice === 'object';
                const name = isObject ? (practice.prac_name || practice.name || practice.prac_code) : practice;
                const code = isObject ? (practice.prac_code || '') : practice;
                const address = isObject ? [practice.address1, practice.address2, practice.city || practice.address5, practice.postcode].filter(a => a && a !== 'NA').join(', ') : '';
                return (
                  <div key={code || index} className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md",
                    theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'bg-muted/20 border border-border'
                  )}>
                    <Building2 className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-accent-foreground truncate">{name}</p>
                      {address && (
                        <p className="text-xs text-muted-foreground truncate">{address}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={cn(
              "text-center py-6 rounded-md mb-4",
              theme === 'light' ? 'bg-gray-50' : 'bg-muted/10'
            )}>
              <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No practices added yet</p>
              <p className="text-xs text-muted-foreground mt-1">Add practices to start creating invoices</p>
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={() => setShowPracticeSettings(true)}
            className={cn(
              "w-full",
              theme === 'light' 
                ? 'text-purple-700 border-purple-300 hover:bg-purple-50' 
                : 'text-purple-300 border-purple-500/50 hover:bg-purple-900/30'
            )}
          >
            <Building2 className="h-4 w-4 mr-2" />
            Manage Practices
          </Button>
        </CollapsibleSection>

        {/* ─── Section 1: Personal Information ─── */}
        <CollapsibleSection
          title="Personal Information"
          subtitle={isPersonalComplete ? 'All details filled' : 'Some details missing'}
          isComplete={isPersonalComplete}
          isOpen={openSections.personal}
          onToggle={() => toggleSection('personal')}
          theme={theme}
        >
        <div className="space-y-4">
          {/* Add general explanation at the top */}
          <div className={cn(
            "bg-muted/20 p-3 rounded-md border",
            theme === 'light' ? 'border-blue-200 bg-blue-50' : 'border-purple-500/20 bg-purple-900/10'
          )}>
            <p className={cn(
              "text-sm",
              theme === 'light' ? 'text-blue-800' : 'text-purple-300'
            )}>
              <strong>Profile Information:</strong> Your details below are used to populate your generated invoices, 
              making billing faster and more professional. All sensitive information is encrypted and stored securely.
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="email" className="text-sm font-medium text-accent-foreground">
              Email
            </Label>
            <Input
              id="email"
              value={profile.email || auth.currentUser?.email || ''}
              disabled
              className="w-full text-primary placeholder:text-muted-foreground"
            />
          </div>

          {/* Role Selection - ENHANCED with better validation styling */}
          <div className="space-y-1">
            <Label htmlFor="role" className="text-sm font-medium text-accent-foreground">
              Professional Role <span className="text-red-500">*</span>
            </Label>
            <select
              id="role"
              value={profile.role || ''}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border border-input bg-background text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                validationErrors.role ? 'border-red-500' : ''
              }`}
              required
            >
              <option value="">Please select your role</option>
              <option value="Dentist">Dentist</option>
              <option value="Hygienist">Hygienist</option>
              <option value="Therapist">Therapist</option>
              <option value="Dental Nurse">Dental Nurse</option>
              <option value="Practice Manager">Practice Manager</option>
            </select>
            {validationErrors.role && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.role}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Your role helps us customize your experience and available features.
            </p>
          </div>

          {fieldOrder.map((key) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={key} className="text-sm font-medium text-accent-foreground">
                {fieldLabels[key]}
                {sensitiveFields.includes(key) && <span className="text-xs text-accent-foreground/70 ml-1">(Encrypted)</span>}
              </Label>
              <Input
                id={key}
                value={profile[key] || ''}
                onChange={handleInputChange}
                className={`w-full text-primary placeholder:text-muted-foreground ${
                  validationErrors[key] ? 'border-red-500' : ''
                }`}
                placeholder={key === 'name' ? 'Enter your full name' : ''}
                required={key === 'name'}
              />
              {validationErrors[key] && (
                <p className="text-red-500 text-xs mt-1">{validationErrors[key]}</p>
              )}
            </div>
          ))}
        </div>
        </CollapsibleSection>

        {/* ─── Section 2: Banking Details ─── */}
        {profile.role !== 'Practice Manager' && (
          <CollapsibleSection
            title="Banking Details"
            subtitle={isBankingComplete ? 'All details filled' : 'Some details missing'}
            isComplete={isBankingComplete}
            isOpen={openSections.banking}
            onToggle={() => toggleSection('banking')}
            theme={theme}
          >
            <p className="text-sm text-muted-foreground mb-4">
              These details are used to populate payment information on your generated invoices. 
              They are encrypted and stored securely, and help practices know where to send your payments.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div className="space-y-1">
                <Label htmlFor="bankName" className="text-sm font-medium text-accent-foreground">
                  Bank Name
                  <span className="text-xs text-accent-foreground/70 block">(Encrypted)</span>
                </Label>
                <Input
                  id="bankName"
                  value={profile.bankName || ''}
                  onChange={handleInputChange}
                  className="w-full text-primary placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sortCode" className="text-sm font-medium text-accent-foreground">
                  Sort Code
                  <span className="text-xs text-accent-foreground/70 block">(Encrypted)</span>
                </Label>
                <Input
                  id="sortCode"
                  value={profile.sortCode || ''}
                  onChange={handleInputChange}
                  className="w-full text-primary placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="accountNumber" className="text-sm font-medium text-accent-foreground">
                  Account Number
                  <span className="text-xs text-accent-foreground/70 block">(Encrypted)</span>
                </Label>
                <Input
                  id="accountNumber"
                  value={profile.accountNumber || ''}
                  onChange={handleInputChange}
                  className="w-full text-primary placeholder:text-muted-foreground"
                />
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* ─── Section 3: Professional Registrations ─── */}
        {profile.role !== 'Practice Manager' && (
          <CollapsibleSection
            title="Professional Registrations"
            subtitle={isRegistrationsComplete ? 'GDC details filled' : 'Registration details needed'}
            isComplete={isRegistrationsComplete}
            isOpen={openSections.registrations}
            onToggle={() => toggleSection('registrations')}
            theme={theme}
          >
            <p className="text-sm text-muted-foreground mb-4">
              Your professional registration details ensure compliance and build trust with practices. 
              We can send you renewal reminders to help maintain your registration status.
            </p>
            {/* GDC */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div className="space-y-1">
                <Label htmlFor="gdcNumber" className="text-sm font-medium text-accent-foreground">
                  GDC Number <span className="text-xs text-accent-foreground/70 ml-1">(Encrypted)</span>
                </Label>
                <Input
                  id="gdcNumber"
                  value={profile.gdcNumber || ''}
                  onChange={handleInputChange}
                  className="w-full text-primary placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="gdcExpiryDate" className="text-sm font-medium text-accent-foreground">
                  GDC Expiry Date
                </Label>
                <Popover open={gdcCalendarOpen} onOpenChange={setGdcCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !profile.gdcExpiryDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {profile.gdcExpiryDate ? format(profile.gdcExpiryDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 app-card-bg border-border" align="start">
                    <SimpleCalendar
                      mode="single" // Keep mode="single" if SimpleCalendar passes it to react-day-picker
                      value={profile.gdcExpiryDate || undefined} // Changed from selected to value
                      onChange={(date) => handleDateChange('gdcExpiryDate', date)} // Changed from onSelect to onChange
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            {/* Insurance */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mt-4">
              <div className="space-y-1 md:col-span-1">
                <Label htmlFor="insuranceProvider" className="text-sm font-medium text-accent-foreground">
                  Indemnity Provider <span className="text-xs text-accent-foreground/70 ml-1">(Encrypted)</span>
                </Label>
                <Input
                  id="insuranceProvider"
                  value={profile.insuranceProvider || ''}
                  onChange={handleInputChange}
                  className="w-full text-primary placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-1 md:col-span-1">
                <Label htmlFor="insurancePolicyNumber" className="text-sm font-medium text-accent-foreground">
                  Policy Number <span className="text-xs text-accent-foreground/70 ml-1">(Encrypted)</span>
                </Label>
                <Input
                  id="insurancePolicyNumber"
                  value={profile.insurancePolicyNumber || ''}
                  onChange={handleInputChange}
                  className="w-full text-primary placeholder:text-muted-foreground"
                />
              </div>
              <div className="space-y-1 md:col-span-1">
                <Label htmlFor="insuranceExpiryDate" className="text-sm font-medium text-accent-foreground">
                  Insurance Expiry Date
                </Label>
                <Popover open={insuranceCalendarOpen} onOpenChange={setInsuranceCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !profile.insuranceExpiryDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {profile.insuranceExpiryDate ? format(profile.insuranceExpiryDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 app-card-bg border-border" align="start">
                    <SimpleCalendar
                      mode="single" // Keep mode="single"
                      value={profile.insuranceExpiryDate || undefined} // Changed from selected to value
                      onChange={(date) => handleDateChange('insuranceExpiryDate', date)} // Changed from onSelect to onChange
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CollapsibleSection>
        )}
        
        {/* ─── Section 4: Communication Preferences ─── */}
        <CollapsibleSection
          title="Communication Preferences"
          subtitle="Email notification settings"
          isComplete={isCommunicationsComplete}
          isOpen={openSections.communications}
          onToggle={() => toggleSection('communications')}
          theme={theme}
        >
          <div className="space-y-3">
            {/* GDC/Insurance reminders - Hidden for Practice Managers */}
            {profile.role !== 'Practice Manager' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="consentPreferences.email.gdcInsuranceReminders"
                  checked={profile.consentPreferences?.email?.gdcInsuranceReminders || false}
                  onCheckedChange={(checked) => handleInputChange({ target: { id: 'consentPreferences.email.gdcInsuranceReminders', checked, type: 'checkbox' }})}
                />
                <Label htmlFor="consentPreferences.email.gdcInsuranceReminders" className="text-sm font-medium text-accent-foreground cursor-pointer">
                  Receive GDC/Insurance renewal reminders by email.
                </Label>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="consentPreferences.email.marketingUpdates"
                checked={profile.consentPreferences?.email?.marketingUpdates || false}
                onCheckedChange={(checked) => handleInputChange({ target: { id: 'consentPreferences.email.marketingUpdates', checked, type: 'checkbox' }})}
              />
              <Label htmlFor="consentPreferences.email.marketingUpdates" className="text-sm font-medium text-accent-foreground cursor-pointer">
                Receive occasional news, tips, and special offers by email.
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="consentPreferences.email.systemMaintenanceUpdates"
                checked={profile.consentPreferences?.email?.systemMaintenanceUpdates === undefined ? true : profile.consentPreferences.email.systemMaintenanceUpdates}
                onCheckedChange={(checked) => handleInputChange({ target: { id: 'consentPreferences.email.systemMaintenanceUpdates', checked, type: 'checkbox' }})}
              />
              <Label htmlFor="consentPreferences.email.systemMaintenanceUpdates" className="text-sm font-medium text-accent-foreground cursor-pointer">
                Receive important system maintenance and service update emails.
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              You can change these preferences at any time. We will always send critical account security emails.
            </p>
          </div>
        </CollapsibleSection>

        <CardFooter className="p-0 pt-6">
          <Button
            type="button"
            onClick={handleSave}
            className="w-full app-accent-purple-bg hover:app-accent-purple-hover text-white"
            disabled={saveStatus === 'Saving...'}
          >
            {saveStatus === 'Saving...' ? 'Saving...' : 'Complete Profile'}
          </Button>
        </CardFooter>

        {error && <div className="text-red-500 mt-2 text-sm text-center">{error}</div>}
        {saveStatus && saveStatus !== 'Saving...' && saveStatus !== 'Save failed' && <div className="text-green-500 mt-2 text-sm text-center">{saveStatus}</div>}
      </CardContent>
    </Card>

    {/* Practice Settings Dialog — PracticeSettings renders its own Dialog */}
    {showPracticeSettings && (
      <PracticeDataProvider>
        <PracticeSettings
          initialSelectedPractices={selectedPractices}
          onSave={handleSavePractices}
          onClose={() => setShowPracticeSettings(false)}
          user={auth.currentUser}
        />
      </PracticeDataProvider>
    )}
    </>
  );
};

export default ProfileSettings;